import { type Font } from '@/shared/service/fontsService'
import {
  type MapPOI,
  type PreviewApplicationConfig,
  type PreviewGrabData,
} from '@/shared/types/applicationData.type'
import { type Program } from '@/shared/types/program.type'

import { type TranslatableText } from '../../../types/TranslatableText.type'
import { type GuestType } from '../guestTypes/GuestType.model'

export type ViewportValue = 'auto' | 1920 | 1440 | 1024

type StaticPageView =
  | 'GUESTS'
  | 'TIMELINE'
  | 'EVENTS'
  | 'GUESTS_COMPACT'
  | 'INFOS_PRATIQUES'
  | 'PARTENAIRES'

// Tile configuration types
export type TileSlotPosition =
  | 'TOP_LEFT'
  | 'TOP_CENTER'
  | 'TOP_RIGHT'
  | 'MIDDLE_LEFT'
  | 'MIDDLE_CENTER'
  | 'MIDDLE_RIGHT'
  | 'BOTTOM_LEFT'
  | 'BOTTOM_CENTER'
  | 'BOTTOM_RIGHT'

export type TileFieldType =
  | 'NONE'
  | 'TITLE'
  | 'TIME'
  | 'DATE_TIME'
  | 'TAGS'
  | 'NAME'
  | 'DESCRIPTION'
  | 'SCENE'
  | 'CONTACT'
  | 'GUEST_TYPE'
  | 'NEXT_EVENT_DATE_TIME'
  | 'NEXT_EVENT_DATE'
  | 'NEXT_EVENT_SCENE'

export type TileSlotFont = {
  family?: string
  size?: number
  weight?: string
  color?: string
}

export type TileSlotConfig = {
  position: TileSlotPosition
  field: TileFieldType
  font?: TileSlotFont
  backgroundColor?: string
  tagCategoryName?: string
  tagCategoryId?: string
}

export type InfoBlockElementType =
  | 'TITLE'
  | 'GUESTS'
  | 'DATE'
  | 'TIME'
  | 'DATE_TIME'
  | 'SCENE'
  | 'PRICE'

export type InfoBlockElementConfig = {
  type: InfoBlockElementType
  enabled: boolean
}

export type TileLayoutConfig = {
  slots: TileSlotConfig[]
  showBackgroundImage?: boolean
  fallbackColor?: string
  showInfoBlock?: boolean
  infoBlockBackgroundColor?: string
  infoBlockElements?: InfoBlockElementConfig[]
  showEventGuests?: boolean
}

export const DEFAULT_INFO_BLOCK_ELEMENTS: InfoBlockElementConfig[] = [
  { type: 'TITLE', enabled: true },
  { type: 'GUESTS', enabled: false },
  { type: 'DATE_TIME', enabled: true },
  { type: 'SCENE', enabled: true },
  { type: 'PRICE', enabled: true },
]

export function getInfoBlockElementTypes(
  tc: (key: string) => string,
  entityType: 'event' | 'guest',
): { value: InfoBlockElementType; label: string; eventOnly?: boolean }[] {
  const all: {
    value: InfoBlockElementType
    label: string
    eventOnly?: boolean
  }[] = [
    { value: 'TITLE', label: tc('STATIC_CFG.LABELS.INFO_BLOCK_TITLE') },
    {
      value: 'GUESTS',
      label: tc('STATIC_CFG.LABELS.INFO_BLOCK_GUESTS'),
      eventOnly: true,
    },
    {
      value: 'DATE_TIME',
      label: tc('STATIC_CFG.LABELS.INFO_BLOCK_DATE_TIME'),
      eventOnly: true,
    },
    {
      value: 'DATE',
      label: tc('STATIC_CFG.LABELS.INFO_BLOCK_DATE'),
      eventOnly: true,
    },
    {
      value: 'TIME',
      label: tc('STATIC_CFG.LABELS.INFO_BLOCK_TIME'),
      eventOnly: true,
    },
    {
      value: 'SCENE',
      label: tc('STATIC_CFG.LABELS.INFO_BLOCK_SCENE'),
      eventOnly: true,
    },
    {
      value: 'PRICE',
      label: tc('STATIC_CFG.LABELS.INFO_BLOCK_PRICE'),
      eventOnly: true,
    },
  ]
  return entityType === 'event' ? all : all.filter((e) => e.eventOnly !== true)
}

export type TileTemplate =
  | 'DEFAULT'
  | 'IMAGE_TOP'
  | 'IMAGE_FULL'
  | 'COMPACT'
  | 'TEXT_ONLY'
  | 'CUSTOM'

export function getTileTemplates(
  tc: (key: string) => string,
): { value: TileTemplate; label: string }[] {
  return [
    { value: 'DEFAULT', label: tc('STATIC_CFG.LABELS.TILE_TPL_DEFAULT') },
    { value: 'IMAGE_TOP', label: tc('STATIC_CFG.LABELS.TILE_TPL_IMAGE_TOP') },
    {
      value: 'IMAGE_FULL',
      label: tc('STATIC_CFG.LABELS.TILE_TPL_IMAGE_FULL'),
    },
    { value: 'COMPACT', label: tc('STATIC_CFG.LABELS.TILE_TPL_COMPACT') },
    {
      value: 'TEXT_ONLY',
      label: tc('STATIC_CFG.LABELS.TILE_TPL_TEXT_ONLY'),
    },
    { value: 'CUSTOM', label: tc('STATIC_CFG.LABELS.TILE_TPL_CUSTOM') },
  ]
}

export const TILE_SLOT_POSITIONS: TileSlotPosition[] = [
  'TOP_LEFT',
  'TOP_CENTER',
  'TOP_RIGHT',
  'MIDDLE_LEFT',
  'MIDDLE_CENTER',
  'MIDDLE_RIGHT',
  'BOTTOM_LEFT',
  'BOTTOM_CENTER',
  'BOTTOM_RIGHT',
]

export function getEventFieldTypes(
  tc: (key: string) => string,
): { value: TileFieldType; label: string }[] {
  return [
    { value: 'NONE', label: tc('STATIC_CFG.LABELS.FIELD_NONE') },
    { value: 'TITLE', label: tc('STATIC_CFG.LABELS.FIELD_TITLE') },
    { value: 'TAGS', label: tc('STATIC_CFG.LABELS.FIELD_TAGS') },
    { value: 'TIME', label: tc('STATIC_CFG.LABELS.FIELD_TIME') },
    { value: 'DATE_TIME', label: tc('STATIC_CFG.LABELS.FIELD_DATE_TIME') },
    { value: 'SCENE', label: tc('STATIC_CFG.LABELS.FIELD_SCENE') },
    { value: 'CONTACT', label: tc('STATIC_CFG.LABELS.FIELD_CONTACT') },
    { value: 'GUEST_TYPE', label: tc('STATIC_CFG.LABELS.FIELD_GUEST_TYPE') },
  ]
}

export function getGuestFieldTypes(
  tc: (key: string) => string,
): { value: TileFieldType; label: string }[] {
  return [
    { value: 'NONE', label: tc('STATIC_CFG.LABELS.FIELD_NONE') },
    { value: 'NAME', label: tc('STATIC_CFG.LABELS.FIELD_NAME') },
    { value: 'TAGS', label: tc('STATIC_CFG.LABELS.FIELD_TAGS') },
    { value: 'CONTACT', label: tc('STATIC_CFG.LABELS.FIELD_CONTACT') },
    { value: 'GUEST_TYPE', label: tc('STATIC_CFG.LABELS.FIELD_GUEST_TYPE') },
    {
      value: 'NEXT_EVENT_DATE_TIME',
      label: tc('STATIC_CFG.LABELS.FIELD_NEXT_EVENT_DATE_TIME'),
    },
    {
      value: 'NEXT_EVENT_DATE',
      label: tc('STATIC_CFG.LABELS.FIELD_NEXT_EVENT_DATE'),
    },
    {
      value: 'NEXT_EVENT_SCENE',
      label: tc('STATIC_CFG.LABELS.FIELD_NEXT_EVENT_SCENE'),
    },
  ]
}

// Detail layout configuration types (for Guest/Event detail view)
export type DetailElementType =
  | 'NAME'
  | 'DESCRIPTION'
  | 'IMAGE'
  | 'TAGS'
  | 'TIME' // Event only - heure seule
  | 'DATE_TIME' // Event only - date et heure
  | 'SCENE' // Event only
  | 'SPOTIFY'
  | 'DEEZER'
  | 'GUESTS'
  | 'COORDS'
  | 'SIMILAR_EVENTS'
  | 'GUEST_TAGS' // Event only - tags des guests liés
  | 'RELATED_EVENTS'
  | 'COPYRIGHT'
  | 'VIDEO' // Guest - YouTube embed
  | 'SOCIALS' // Guest - social media links
  | 'CONTACT'
  | 'GUEST_TYPE'
  | 'NEXT_EVENT_DATE_TIME' // Guest uniquement - prochain passage date et heure
  | 'NEXT_EVENT_DATE' // Guest uniquement - prochain passage date
  | 'NEXT_EVENT_SCENE' // Guest uniquement - prochain passage lieu

export type DetailElementConfig = {
  type: DetailElementType
  font?: TileSlotFont
  backgroundColor?: string
  tagCategoryName?: string
  tagCategoryId?: string
  sectionTitle?: TranslatableText
  // GUESTS only — restrict the displayed guests to these guest type ids.
  // Empty/undefined = show all guest types (default).
  guestTypes?: GuestType['_id'][]
}

type DetailBannerConfig = {
  backgroundColor?: string
  element?: DetailElementConfig
}

export type DetailColumnConfig = {
  widthPercent: number // 0-100, if 0 or empty the column disappears
  elements: DetailElementConfig[]
}

export type DetailLayoutConfig = {
  banner?: DetailBannerConfig
  columns: DetailColumnConfig[]
}

export function getEventDetailElementTypes(
  tc: (key: string) => string,
): { value: DetailElementType; label: string }[] {
  return [
    { value: 'NAME', label: tc('STATIC_CFG.LABELS.FIELD_NAME') },
    { value: 'DESCRIPTION', label: tc('STATIC_CFG.LABELS.FIELD_DESCRIPTION') },
    { value: 'IMAGE', label: tc('STATIC_CFG.LABELS.FIELD_IMAGE') },
    { value: 'TAGS', label: tc('STATIC_CFG.LABELS.FIELD_TAGS') },
    { value: 'TIME', label: tc('STATIC_CFG.LABELS.FIELD_TIME') },
    { value: 'DATE_TIME', label: tc('STATIC_CFG.LABELS.FIELD_DATE_TIME') },
    { value: 'SCENE', label: tc('STATIC_CFG.LABELS.FIELD_SCENE') },
    { value: 'SPOTIFY', label: tc('STATIC_CFG.LABELS.FIELD_SPOTIFY') },
    { value: 'DEEZER', label: tc('STATIC_CFG.LABELS.FIELD_DEEZER') },
    { value: 'GUESTS', label: tc('STATIC_CFG.LABELS.FIELD_GUESTS') },
    { value: 'COORDS', label: tc('STATIC_CFG.LABELS.FIELD_COORDS') },
    {
      value: 'SIMILAR_EVENTS',
      label: tc('STATIC_CFG.LABELS.FIELD_SIMILAR_EVENTS'),
    },
    { value: 'GUEST_TAGS', label: tc('STATIC_CFG.LABELS.FIELD_GUEST_TAGS') },
    {
      value: 'RELATED_EVENTS',
      label: tc('STATIC_CFG.LABELS.FIELD_RELATED_EVENTS'),
    },
    { value: 'COPYRIGHT', label: tc('STATIC_CFG.LABELS.FIELD_COPYRIGHT') },
    { value: 'CONTACT', label: tc('STATIC_CFG.LABELS.FIELD_CONTACT') },
    { value: 'GUEST_TYPE', label: tc('STATIC_CFG.LABELS.FIELD_GUEST_TYPE') },
  ]
}

export function getGuestDetailElementTypes(
  tc: (key: string) => string,
): { value: DetailElementType; label: string }[] {
  return [
    { value: 'NAME', label: tc('STATIC_CFG.LABELS.FIELD_NAME') },
    { value: 'DESCRIPTION', label: tc('STATIC_CFG.LABELS.FIELD_DESCRIPTION') },
    { value: 'IMAGE', label: tc('STATIC_CFG.LABELS.FIELD_IMAGE') },
    { value: 'TAGS', label: tc('STATIC_CFG.LABELS.FIELD_TAGS') },
    { value: 'DEEZER', label: tc('STATIC_CFG.LABELS.FIELD_DEEZER') },
    { value: 'SPOTIFY', label: tc('STATIC_CFG.LABELS.FIELD_SPOTIFY') },
    { value: 'VIDEO', label: tc('STATIC_CFG.LABELS.FIELD_VIDEO') },
    { value: 'SOCIALS', label: tc('STATIC_CFG.LABELS.FIELD_SOCIALS') },
    { value: 'COORDS', label: tc('STATIC_CFG.LABELS.FIELD_COORDS') },
    {
      value: 'RELATED_EVENTS',
      label: tc('STATIC_CFG.LABELS.FIELD_RELATED_EVENTS'),
    },
    { value: 'COPYRIGHT', label: tc('STATIC_CFG.LABELS.FIELD_COPYRIGHT') },
    { value: 'CONTACT', label: tc('STATIC_CFG.LABELS.FIELD_CONTACT') },
    { value: 'GUEST_TYPE', label: tc('STATIC_CFG.LABELS.FIELD_GUEST_TYPE') },
    {
      value: 'NEXT_EVENT_DATE_TIME',
      label: tc('STATIC_CFG.LABELS.FIELD_NEXT_EVENT_DATE_TIME'),
    },
    {
      value: 'NEXT_EVENT_DATE',
      label: tc('STATIC_CFG.LABELS.FIELD_NEXT_EVENT_DATE'),
    },
    {
      value: 'NEXT_EVENT_SCENE',
      label: tc('STATIC_CFG.LABELS.FIELD_NEXT_EVENT_SCENE'),
    },
  ]
}

export const LANGS = ['fr', 'en'] as const
export type Lang = (typeof LANGS)[number]

type StaticPreviewGrabData = Pick<
  PreviewGrabData,
  | 'events'
  | 'genres'
  | 'groups'
  | 'guestTypes'
  | 'programs'
  | 'scenes'
  | 'infoPratiques'
  | 'sponsorCategories'
  | 'sponsors'
> & {
  poiList: MapPOI[]
  staticConfig: Pick<PreviewApplicationConfig, 'timezone'> & {
    eventsHideLocation: boolean
    features?: { activatePoweredBy: boolean }
  }
  pages: StaticPreviewPage[]
}

type StaticPreviewPage = {
  blockName: string
}

export type StaticPreviewDataSets = Record<
  Lang,
  StaticPreviewGrabData | undefined
>

type StaticStyles = {
  colorTitle: string
  colorBackground: string
  colorBlockBackground: string
  colorBlockText: string
  colorAction: string
  colorBlockAction: string
}

export type FontChangeSet = {
  fontText: File | undefined
  fontTitle: File | undefined
}

// type stored in back
export type StaticConfig = StaticStyles & {
  _id?: string
  label?: string
  fontTitle: Font | null | undefined
  fontText: Font | null | undefined
  smartBannerEnabled: boolean
  pages?: StaticPage[]
  locales?: Lang[]
  eventsHideLocation?: boolean
  // Legacy fields: previously global, now per-page. Kept for backward compatibility with older configs.
  alwaysShowEventHour?: boolean
  showEventHourDelay?: number
  eventsHideEndDate?: boolean
  orderEventsByEndDate?: boolean
}

export const DEFAULT_HOME_PAGE_CARDS_PER_ROW = 4

export type StaticPage = {
  icon: string
  default: boolean
  visible: boolean
  view: StaticPageView
  blockName: TranslatableText
  guestTypes?: GuestType['_id'][]
  programs?: Program['_id'][]
  infoPratiqueIds?: string[]
  infoPratiquesExpanded?: boolean
  infoPratiquesHideImages?: boolean
  infoPratiquesHideTitle?: boolean
  cardsPerRow?: number
  centerIcons?: boolean
  tileTemplate?: TileTemplate
  tileLayout?: TileLayoutConfig
  detailLayout?: DetailLayoutConfig
  defaultDaySelection?: DefaultDaySelection
  showEventDates?: boolean
  showEventScenes?: boolean
  shortcutFilters?: ShortcutFilterConfig[]
  alwaysShowEventHour?: boolean
  showEventHourDelay?: number
  eventsHideEndDate?: boolean
  orderEventsByEndDate?: boolean
}

type TranslationUnit = { [key: string]: string | TranslationUnit }

export type DefaultDaySelection = 'none' | 'closest'

export function getDefaultDaySelectionOptions(
  tc: (key: string) => string,
): { value: DefaultDaySelection; label: string }[] {
  return [
    { value: 'closest', label: tc('STATIC_CFG.LABELS.DAY_CLOSEST') },
    { value: 'none', label: tc('STATIC_CFG.LABELS.DAY_NONE') },
  ]
}

// Shortcut filter configuration - displays specific tags as quick-access buttons or dropdown
export type ShortcutFilterDisplayMode = 'buttons' | 'dropdown'

export type ShortcutFilterType = 'tags' | 'scenes' | 'programs'

export type ShortcutFilterConfig = {
  categoryName: string
  categoryId?: string
  label?: TranslatableText // Custom display name shown as placeholder in dropdown, per language
  tagIds: string[] // The specific tag IDs, scene IDs or program IDs to show as shortcut buttons
  displayMode?: ShortcutFilterDisplayMode
  type?: ShortcutFilterType // 'tags' (default), 'scenes' or 'programs'
}

type SpecificFestivalSettings = {
  i18n?: Record<Lang, TranslationUnit>
  locales?: Lang[]
  eventsHideLocation?: boolean
  defaultDaySelection?: DefaultDaySelection
  // Legacy fields: kept for backward compatibility with older backoffice config page
  alwaysShowEventHour?: boolean
  showEventHourDelay?: number
  eventsHideEndDate?: boolean
  orderEventsByEndDate?: boolean
}

// attributes passed to the <chapito-static> web component
export type StaticWebComponentAttributes = {
  pages?: StaticPage[]
  'specific-settings'?: SpecificFestivalSettings
  styles?: StaticStyles
  data?: StaticPreviewDataSets
}
